import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  ArrowClockwise,
  Globe,
  Keyboard,
  ArrowsIn,
  ArrowsOut,
  ShieldCheck,
  LockKey,
  CursorClick,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEY_MAP = {
  Enter: "Enter",
  Backspace: "Backspace",
  Tab: "Tab",
  Escape: "Escape",
  ArrowUp: "ArrowUp",
  ArrowDown: "ArrowDown",
  ArrowLeft: "ArrowLeft",
  ArrowRight: "ArrowRight",
  Delete: "Delete",
  Home: "Home",
  End: "End",
  " ": "Space",
};

const BrowserViewer = ({ profileId, profileName, profileOzon, onClose }) => {
  const [screenshot, setScreenshot] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [browserInfo, setBrowserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navUrl, setNavUrl] = useState("");
  const [scale, setScale] = useState(0.75);
  const [keyboardActive, setKeyboardActive] = useState(true);
  const [clickIndicator, setClickIndicator] = useState(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);
  const navInputRef = useRef(null);

  const fetchScreenshot = useCallback(async () => {
    try {
      const [ssRes, infoRes] = await Promise.all([
        axios.get(`${API}/browser/${profileId}/screenshot`),
        axios.get(`${API}/browser/${profileId}/info`),
      ]);
      setScreenshot(`data:image/jpeg;base64,${ssRes.data.image}`);
      setPageInfo(infoRes.data.page);
      setBrowserInfo(infoRes.data);
      if (!navUrl || loading) setNavUrl(infoRes.data.page?.url || "");
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [profileId, navUrl, loading]);

  useEffect(() => {
    fetchScreenshot();
    intervalRef.current = setInterval(fetchScreenshot, 700);
    return () => clearInterval(intervalRef.current);
  }, [fetchScreenshot]);

  // Direct keyboard passthrough to browser
  useEffect(() => {
    if (!keyboardActive) return;

    const handler = (e) => {
      // Don't capture if typing in nav URL bar
      if (document.activeElement === navInputRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const mapped = KEY_MAP[e.key];
      if (mapped) {
        axios.post(`${API}/browser/${profileId}/key`, { key: mapped });
      } else if (e.key.length === 1) {
        // Regular character
        axios.post(`${API}/browser/${profileId}/type`, { text: e.key });
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [profileId, keyboardActive]);

  const handleClick = async (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const natW = imgRef.current.naturalWidth;
    const natH = imgRef.current.naturalHeight;

    const x = Math.round(((e.clientX - rect.left) / rect.width) * natW);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * natH);

    // Visual click indicator
    setClickIndicator({ left: e.clientX, top: e.clientY });
    setTimeout(() => setClickIndicator(null), 400);

    await axios.post(`${API}/browser/${profileId}/click`, { x, y });
    setTimeout(fetchScreenshot, 200);
  };

  const handleScroll = (e) => {
    e.preventDefault();
    axios.post(`${API}/browser/${profileId}/scroll`, {
      deltaX: e.deltaX,
      deltaY: e.deltaY,
    });
  };

  const handleNav = async (e) => {
    e.preventDefault();
    if (!navUrl) return;
    let url = navUrl;
    if (!url.startsWith("http")) url = "https://" + url;
    await axios.post(`${API}/browser/${profileId}/navigate`, { url });
    setTimeout(fetchScreenshot, 500);
  };

  const handleFillPin = async () => {
    const pin = profileOzon?.pincode;
    if (!pin) return;
    await axios.post(`${API}/browser/${profileId}/fill-pin`, { pin });
    setTimeout(fetchScreenshot, 500);
  };

  const handleBack = () => axios.post(`${API}/browser/${profileId}/back`).then(() => setTimeout(fetchScreenshot, 400));
  const handleForward = () => axios.post(`${API}/browser/${profileId}/forward`).then(() => setTimeout(fetchScreenshot, 400));
  const handleReload = () => axios.post(`${API}/browser/${profileId}/reload`).then(() => setTimeout(fetchScreenshot, 800));

  const pincode = profileOzon?.pincode;

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent
        className="bg-[#0D0F12] border-[#252A33] p-0 max-w-[95vw] w-[1300px]"
        style={{ maxHeight: "95vh" }}
        data-testid="browser-viewer-modal"
      >
        <DialogTitle className="sr-only">Браузер — {profileName}</DialogTitle>

        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#1A1D24] border-b border-[#252A33] rounded-t-lg">
          <ShieldCheck size={16} className="text-[#4CAF50]" />
          <span className="text-xs font-semibold text-[#4CAF50]">LIVE</span>
          <span className="text-xs text-[#5C6370]">|</span>
          <span className="text-xs font-medium">{profileName}</span>

          {/* PIN button */}
          {pincode && (
            <button
              onClick={handleFillPin}
              className="ml-3 flex items-center gap-1.5 bg-[#FACC15] hover:bg-[#EAB308] text-black text-[11px] font-bold px-3 py-1 rounded transition-colors"
              data-testid="fill-pin-btn"
              title={`Ввести пинкод: ${pincode}`}
            >
              <LockKey size={13} weight="bold" />
              Ввести PIN: {pincode}
            </button>
          )}

          {/* Keyboard toggle */}
          <button
            onClick={() => setKeyboardActive(!keyboardActive)}
            className={`ml-2 flex items-center gap-1 text-[11px] px-2 py-1 rounded transition-colors ${
              keyboardActive
                ? "bg-[#2196F3]/20 text-[#2196F3] border border-[#2196F3]/40"
                : "bg-[#252A33] text-[#5C6370]"
            }`}
            data-testid="keyboard-toggle-btn"
          >
            <Keyboard size={13} />
            {keyboardActive ? "Клавиатура ON" : "Клавиатура OFF"}
          </button>

          {browserInfo?.settings && (
            <div className="flex items-center gap-1.5 ml-auto mr-8">
              {browserInfo.settings.map((s, i) => (
                <span key={i} className="text-[9px] bg-[#252A33] text-[#5C6370] px-1.5 py-0.5 rounded whitespace-nowrap">
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Nav bar */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#13161B] border-b border-[#252A33]">
          <button onClick={handleBack} className="browser-nav-btn" data-testid="browser-back-btn">
            <ArrowLeft size={14} />
          </button>
          <button onClick={handleForward} className="browser-nav-btn" data-testid="browser-forward-btn">
            <ArrowRight size={14} />
          </button>
          <button onClick={handleReload} className="browser-nav-btn" data-testid="browser-reload-btn">
            <ArrowClockwise size={14} />
          </button>
          <form onSubmit={handleNav} className="flex-1 flex">
            <div className="flex items-center flex-1 bg-[#0D0F12] rounded border border-[#252A33] px-2 h-7">
              <Globe size={12} className="text-[#5C6370] mr-1.5 shrink-0" />
              <input
                ref={navInputRef}
                type="text"
                value={navUrl}
                onChange={(e) => setNavUrl(e.target.value)}
                className="flex-1 bg-transparent text-xs outline-none text-[#C8CCD0]"
                placeholder="URL..."
                data-testid="browser-url-input"
              />
            </div>
          </form>
          <button onClick={() => setScale((s) => Math.min(1, s + 0.1))} className="browser-nav-btn">
            <ArrowsOut size={14} />
          </button>
          <button onClick={() => setScale((s) => Math.max(0.3, s - 0.1))} className="browser-nav-btn">
            <ArrowsIn size={14} />
          </button>
        </div>

        {/* Browser viewport */}
        <div
          ref={containerRef}
          className="overflow-auto bg-[#0D0F12] flex items-start justify-center relative"
          style={{ height: "calc(95vh - 110px)" }}
          onWheel={handleScroll}
        >
          {loading ? (
            <div className="flex items-center justify-center h-full w-full">
              <div className="text-center">
                <ArrowClockwise size={32} className="animate-spin text-[#2196F3] mx-auto mb-3" />
                <p className="text-sm text-[#8A9099]">Загрузка...</p>
              </div>
            </div>
          ) : screenshot ? (
            <img
              ref={imgRef}
              src={screenshot}
              alt="Browser"
              onClick={handleClick}
              className="cursor-pointer select-none"
              style={{
                transform: `scale(${scale})`,
                transformOrigin: "top center",
              }}
              data-testid="browser-screenshot"
              draggable={false}
            />
          ) : (
            <div className="flex items-center justify-center h-full w-full">
              <p className="text-sm text-[#5C6370]">Нет скриншота</p>
            </div>
          )}

          {/* Click indicator */}
          {clickIndicator && (
            <div
              className="fixed pointer-events-none z-50"
              style={{ left: clickIndicator.left - 10, top: clickIndicator.top - 10 }}
            >
              <div className="w-5 h-5 rounded-full border-2 border-[#2196F3] animate-ping" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-1 bg-[#1A1D24] border-t border-[#252A33] rounded-b-lg">
          <div className="flex items-center gap-2 text-[10px] text-[#5C6370]">
            <CursorClick size={12} />
            <span>{keyboardActive ? "Клавиатура активна — нажимайте клавиши для ввода" : "Кликайте по скриншоту"}</span>
          </div>
          <div className="text-[10px] text-[#5C6370] truncate max-w-md">
            {pageInfo?.title}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BrowserViewer;
